import { Bell } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { getNotificationMessage, getNotifications, getUnreadCount, markAllAsRead, subscribeToNotifications } from '../lib/notifications'
import type { Notification } from '../lib/types'

export function NotificationBell() {
  const { userId } = useAuth()
  const [unread, setUnread] = useState(0)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const loadUnread = useCallback(async () => {
    if (!userId) return
    const count = await getUnreadCount(userId)
    setUnread(count)
  }, [userId])

  const loadNotifications = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    const data = await getNotifications(userId, 8)
    setNotifications(data)
    setLoading(false)
  }, [userId])

  useEffect(() => {
    loadUnread()
    if (!userId) return
    const unsubscribe = subscribeToNotifications(userId, () => {
      loadUnread()
    })
    return unsubscribe
  }, [userId, loadUnread])

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  async function handleOpen() {
    if (!open) {
      await loadNotifications()
    }
    setOpen(!open)
  }

  async function handleMarkAllRead() {
    if (!userId) return
    await markAllAsRead(userId)
    setUnread(0)
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }

  if (!userId) return null

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={handleOpen}
        className="relative flex h-10 w-10 items-center justify-center rounded-xl text-crusta-700 hover:bg-crusta-50"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-80 overflow-hidden rounded-2xl border border-crusta-100 bg-white shadow-[0_18px_38px_rgba(155,43,17,0.12)]">
          <div className="flex items-center justify-between border-b border-crusta-100 px-4 py-3">
            <p className="text-sm font-extrabold text-crusta-950">Notifications</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-xs font-bold text-crusta-600 hover:text-crusta-800"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 3 }, (_, i) => (
                  <div key={i} className="h-12 animate-pulse rounded-xl bg-crusta-100" />
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-crusta-700">No notifications yet</div>
            ) : (
              <div className="divide-y divide-crusta-50">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 transition-colors ${notification.read ? '' : 'bg-crusta-50/50'}`}
                  >
                    <p className="text-xs leading-relaxed text-crusta-900">
                      {getNotificationMessage(notification)}
                    </p>
                    <p className="mt-1 text-[10px] text-crusta-600">
                      {new Date(notification.created_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block border-t border-crusta-100 px-4 py-3 text-center text-xs font-bold text-crusta-600 hover:text-crusta-800"
          >
            View all notifications
          </Link>
        </div>
      )}
    </div>
  )
}
